import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { ErrorType } from '../../../../../../domain/entities/Auth';
import {
  LoginQuery,
  LoginWithFbQuery,
} from '../../../../../../infrastructure/repositories/AuthRepository';
import FACEBOOK_API_ID from '../../../../../../utils/facebook-api-id';
import PurchaseFacebookLoginButton from '../../common/buttons/PurchaseFacebookLoginButton';
import PurchaseSubmitButton from '../../common/buttons/PurchaseSubmitButton';
import PurchaseTextInput, {
  TextInputType,
} from '../../common/forms/PurchaseTextInput';
import PurchaseLabeledContainer from '../../common/PurchaseLabeledContainer';
import * as $ from '../../helper';
import ErrorMessage from './ErrorMessage';

interface Props {
  postable: boolean;
  onLogin: (query: LoginQuery) => void;
  onLoginWithFb: (query: LoginWithFbQuery) => void;
  errors?: ErrorType[];
}

interface Control {
  submittable?: boolean;
}

interface State {
  email: string;
  password: string;
  control: Control;
}

interface ResponseFacebookMe {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
  }
}

class InputLogin extends React.Component<Props & InjectedIntlProps, State> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleFacebookSubmit = this.handleFacebookSubmit.bind(this);
    const _state: State = {
      email: '',
      password: '',
      control: {},
    };
    this.state = {
      ..._state,
      control: this.createControl(_state),
    };
  }

  public componentDidMount() {
    this.loadFbLoginApi();
  }

  private loadFbLoginApi() {
    window.fbAsyncInit = () => {
      FB.init({
        appId: FACEBOOK_API_ID,
        cookie: true,
        xfbml: true,
        version: 'v4.0',
      });
      FB.AppEvents.logPageView();
    };
    (d => {
      const fjs = d.getElementsByTagName('script')[0];
      if (d.getElementById('facebook-jssdk')) {
        return;
      }
      const js = d.createElement('script');
      js.id = 'facebook-jssdk';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode!.insertBefore(js, fjs);
    })(document);
  }

  private createControl(state: State): Control {
    const { control } = state;
    control.submittable =
      $.getValueByNestedString(state, 'email') &&
      $.getValueByNestedString(state, 'password');
    return control;
  }

  private handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const {
      currentTarget: { name, value },
    } = e;
    const changedState = $.changeObjectByNestedString(this.state, name, value);
    this.setState({
      ...changedState,
      control: this.createControl(changedState as State),
    });
  }

  private async handleSubmit() {
    const { email, password } = this.state;
    this.props.onLogin({ email, password });
  }

  private async handleFacebookSubmit() {
    FB.login(
      resLogin => {
        if (resLogin.authResponse) {
          FB.api(
            '/me?fields=email,first_name,last_name',
            (resMe: ResponseFacebookMe) => {
              const query = {
                email: resMe.email,
                id: resMe.id,
                accessToken: resLogin.authResponse.accessToken,
                firstName: resMe.first_name,
                lastName: resMe.last_name,
                expireAt: resLogin.authResponse.expiresIn,
              };
              this.props.onLoginWithFb(query);
            },
          );
        }
      },
      { scope: 'email' },
    );
  }

  public render() {
    const { postable, intl, errors } = this.props;
    const {
      control: { submittable },
    } = this.state;

    return (
      <div>
        <PurchaseLabeledContainer
          label={intl.formatMessage({
            id: 'purchases.inputLogin.email',
          })}
          paddingTop={true}
        >
          <PurchaseTextInput
            type={TextInputType.Email}
            name={'email'}
            value={this.state.email}
            onChange={this.handleChange}
          />
        </PurchaseLabeledContainer>
        <PurchaseLabeledContainer
          label={intl.formatMessage({
            id: 'purchases.inputLogin.password',
          })}
        >
          <PurchaseTextInput
            type={TextInputType.Password}
            name={'password'}
            value={this.state.password}
            onChange={this.handleChange}
          />
        </PurchaseLabeledContainer>
        <ErrorMessage errors={errors} />
        <PurchaseSubmitButton
          type="button"
          onClick={this.handleSubmit}
          value={intl.formatMessage({ id: 'purchases.inputLogin.submitLogin' })}
          disabled={!submittable || !postable}
          postable={postable}
        >
          {intl.formatMessage({ id: 'purchases.inputLogin.submitLogin' })}
        </PurchaseSubmitButton>
        <PurchaseFacebookLoginButton
          type="button"
          onClick={this.handleFacebookSubmit}
          value={intl.formatMessage({
            id: 'purchases.inputLogin.submitFacebookLogin',
          })}
          disabled={!postable}
          postable={postable}
        >
          {intl.formatMessage({
            id: 'purchases.inputLogin.submitFacebookLogin',
          })}
        </PurchaseFacebookLoginButton>
      </div>
    );
  }
}

export default injectIntl(InputLogin);
